import StoreScopeTag from "@/components/partner/StoreScopeTag";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plug, CheckCircle2 } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { toast } from "sonner";

/**
 * Iter-398 — Doshii POS connection card (vendor self-service).
 *
 * Contract (verified live on the shared backend):
 *   GET  /api/classifieds/engagement/vendors/me
 *        → { pos_status?, pos_provider?, doshii_location_id?, pos_connected_at? }
 *          (fields absent until the vendor first connects)
 *   POST /api/classifieds/engagement/vendors/me/pos/connect
 *        body { location_id } → { pos_provider, doshii_location_id, pos_status: "connected" }
 *        bad venue → 4xx { detail: "Doshii rejected this LocationID — check it in your Doshii dashboard" }
 *   POST /api/classifieds/engagement/vendors/me/pos/disconnect
 *        → { pos_status: "disconnected" }
 *
 * All /engagement/* calls send X-App-Platform + ?platform per contract.
 */

const ENGAGEMENT_HEADERS = { "X-App-Platform": "nz_classified" };
const ENGAGEMENT_PARAM = { platform: "nz_classified" };
const OPTS = { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM };

function formatDateNZ(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-NZ", { day: "2-digit", month: "long", year: "numeric" });
}

export default function PosConnectionCard() {
  const qc = useQueryClient();
  const [locationId, setLocationId] = useState("");
  const [inlineError, setInlineError] = useState("");

  const meQ = useQuery({
    queryKey: ["vendor-me"],
    retry: false,
    queryFn: async () => {
      const { data } = await sharedApi.get(
        "/classifieds/engagement/vendors/me",
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
  });

  const errStatus = meQ.error?.response?.status;
  const notAVendor = meQ.isError && [401, 403, 404].includes(errStatus);

  const connected = meQ.data?.pos_status === "connected";
  // Iter-412 §1 — a referral-based setup link in flight.
  const referral = meQ.data?.pos_referral || null;
  const referralPending = !connected && referral?.status === "pending";

  const referralMut = useMutation({
    mutationFn: async () =>
      (await sharedApi.post("/classifieds/engagement/vendors/me/pos/referral", {}, OPTS)).data,
    onSuccess: () => {
      setInlineError("");
      toast.success("Setup link sent — check your business email.");
      qc.invalidateQueries({ queryKey: ["vendor-me"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Couldn't send the setup link — please try again.";
      setInlineError(msg);
      toast.error(msg);
    },
  });

  const checkMut = useMutation({
    mutationFn: async () =>
      (await sharedApi.post("/classifieds/engagement/vendors/me/pos/referral/check", {}, OPTS)).data,
    onSuccess: (data) => {
      if (data?.pos_status === "connected") {
        toast.success("POS connected — till bills will now pre-fill from Doshii.");
        qc.invalidateQueries({ queryKey: ["vendor-me"] });
      } else {
        toast.info("Not connected yet — finish the Doshii setup link first, then check again.");
      }
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't check the status — please try again.");
    },
  });

  const connectMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(
        "/classifieds/engagement/vendors/me/pos/connect",
        { location_id: locationId.trim() },
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
    onSuccess: () => {
      setInlineError("");
      setLocationId("");
      toast.success("POS connected — till bills will now pre-fill from Doshii.");
      qc.invalidateQueries({ queryKey: ["vendor-me"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : "Couldn't connect — please try again.";
      setInlineError(msg);
      toast.error(msg);
    },
  });

  const disconnectMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post(
        "/classifieds/engagement/vendors/me/pos/disconnect",
        {},
        { headers: ENGAGEMENT_HEADERS, params: ENGAGEMENT_PARAM },
      );
      return data;
    },
    onSuccess: () => {
      toast.success("POS disconnected.");
      qc.invalidateQueries({ queryKey: ["vendor-me"] });
    },
    onError: (err) => {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't disconnect — please try again.");
    },
  });

  if (notAVendor || meQ.isLoading || meQ.isError) return null;

  return (
    <section data-testid="pos-connection-card" className="mt-6 rounded-2xl bg-white shadow-card p-6 md:p-8">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[color:var(--brand-blue-wash)] text-[color:var(--brand-blue)] grid place-items-center">
          <Plug className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-2xl font-black text-slate-900">POS connection</h2>
            <StoreScopeTag />
            {connected ? (
              <span
                data-testid="pos-status-badge"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-widest"
              >
                <CheckCircle2 className="w-3.5 h-3.5" /> Connected
              </span>
            ) : (
              <span
                data-testid="pos-status-badge"
                className="inline-flex items-center rounded-full bg-slate-100 text-slate-500 border border-slate-200 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-widest"
              >
                Not connected
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Link your Doshii-connected point of sale so customer bills pre-fill automatically at the till — no manual amount entry.
          </p>
        </div>
      </div>

      {connected ? (
        <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/60 px-5 py-4 flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700">Doshii LocationID</div>
            <div className="mt-0.5 font-mono text-sm font-bold text-slate-900" data-testid="pos-location-id">
              {meQ.data?.doshii_location_id}
            </div>
            {meQ.data?.pos_connected_at && (
              <div className="mt-0.5 text-[11px] text-slate-500" data-testid="pos-connected-since">
                Connected since {formatDateNZ(meQ.data.pos_connected_at)}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => disconnectMut.mutate()}
            disabled={disconnectMut.isPending}
            data-testid="pos-disconnect-btn"
            className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-red-50 text-red-600 border border-red-200 text-xs font-bold px-4 py-2 shadow-sm transition-colors disabled:opacity-60"
          >
            {disconnectMut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Disconnect
          </button>
        </div>
      ) : referralPending ? (
        <div className="mt-5 rounded-2xl border border-sky-200 bg-sky-50/60 px-5 py-4" data-testid="pos-referral-pending">
          <p className="text-sm text-slate-700 leading-relaxed">
            📨 Setup link sent to{" "}
            <span className="font-bold text-slate-900">
              {meQ.data?.business_email || meQ.data?.contact_email || meQ.data?.email || "your business email"}
            </span>{" "}
            — open it, authorise your POS, and your store connects automatically.
          </p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {referral?.url && (
              <a
                href={referral.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="pos-referral-open-link"
                className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white text-xs font-bold px-4 py-2 shadow-card transition-colors"
              >
                🔗 Open my Doshii setup link
              </a>
            )}
            <button
              type="button"
              onClick={() => checkMut.mutate()}
              disabled={checkMut.isPending}
              data-testid="pos-referral-check-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 text-xs font-bold px-4 py-2 shadow-sm transition-colors disabled:opacity-60"
            >
              {checkMut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              🔄 Check connection status
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-5 space-y-5">
          {/* Iter-412 §1 — referral flow is the PRIMARY path. */}
          <div>
            <button
              type="button"
              onClick={() => referralMut.mutate()}
              disabled={referralMut.isPending}
              data-testid="pos-referral-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold px-5 py-2.5 text-sm shadow-card transition-colors disabled:opacity-60"
            >
              {referralMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              📨 Set up my POS connection
            </button>
            <p className="mt-2 text-[11px] text-slate-400 max-w-xl">
              We&apos;ll send your business details to Doshii and email you a pre-filled setup link — just authorise your POS and you&apos;re connected.
            </p>
            {inlineError && (
              <p className="mt-2 text-xs font-semibold text-red-600" data-testid="pos-connect-error">
                {inlineError}
              </p>
            )}
          </div>

          {/* Fallback — manual LocationID. */}
          <div className="pt-4 border-t border-slate-100">
            <div className="text-sm font-bold text-slate-800">Already have a Doshii LocationID?</div>
            <p className="mt-0.5 text-[11px] text-slate-400 max-w-xl">
              It&apos;s in your Doshii dashboard (dashboard.doshii.io) — not in your POS settings.
            </p>
            <label className="block max-w-xl mt-2.5">
              <div className="flex items-stretch gap-2">
                <input
                  type="text"
                  value={locationId}
                  onChange={(e) => { setLocationId(e.target.value); setInlineError(""); }}
                  placeholder="e.g. NYgmJrRjP"
                  data-testid="pos-location-input"
                  className="flex-1 rounded-xl border border-soft px-3 py-2 text-sm font-mono focus:outline-none focus:border-[color:var(--brand-blue)]"
                />
                <button
                  type="button"
                  onClick={() => connectMut.mutate()}
                  disabled={connectMut.isPending || !locationId.trim()}
                  data-testid="pos-connect-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 font-bold px-4 py-2 text-xs shadow-sm transition-colors disabled:opacity-60"
                >
                  {connectMut.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  🔌 Connect with LocationID
                </button>
              </div>
            </label>
          </div>
        </div>
      )}
    </section>
  );
}
